// Assessment Access — Admin/QM manage time-limited, whitelist-only assessor portal sessions.
// Auditor blocked; other office roles view-only. Tokens are generated client-side; only the sha256 hash is
// stored (raw token lives only in the copied link). No assessor accounts, no app access for assessors.
import React, { useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../lib/AuthContext'
import { format, parseISO } from 'date-fns'
import { isOtherService } from '../lib/service'
import { toast, failed } from '../lib/toast'

function fmt(d) { try { return d ? format(parseISO(d), 'd MMM yyyy') : '—' } catch { return '—' } }
function fmtT(d) { try { return d ? format(parseISO(d), 'd MMM yyyy, HH:mm') : '—' } catch { return '—' } }
function randomToken() { const a = new Uint8Array(32); crypto.getRandomValues(a); return Array.from(a, b => b.toString(16).padStart(2, '0')).join('') }
async function sha256hex(s) { const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(s)); return Array.from(new Uint8Array(buf), b => b.toString(16).padStart(2, '0')).join('') }
function effStatus(s) { if (s.status === 'Revoked') return 'Revoked'; if (s.status === 'Draft') return 'Draft'; if (s.valid_until && new Date(s.valid_until) < new Date()) return 'Expired'; return 'Active' }

// The nine records SAC samples for a ConSASS audit, in order. [slot key, display label].
const CONSASS_DOC_TYPES = [
  ['application_form',       'Application form'],
  ['signed_quotation',       'Signed quotation'],
  ['audit_plan',             'Audit plan'],
  ['auditor_declarations',   "Auditors' declarations"],
  ['confirmation_email',     'Audit confirmation email'],
  ['report_review_approval', 'Audit report review & approval'],
  ['audit_report',           'Audit report'],
  ['attendance_record',      'Attendance record'],
  ['mom_submission',         'Successful submission of report (MOM)'],
]
const CONSASS_BUCKET = 'assessment-uploads'

export default function AssessmentAccess() {
  const { isAuditor } = useAuth()
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [openId, setOpenId] = useState(null)

  useEffect(() => { if (!isAuditor) load() }, [isAuditor])
  async function load() {
    setLoading(true)
    const { data, error } = await supabase.from('assessment_access_sessions').select('*').order('created_at', { ascending: false })
    if (error) console.warn('assessment_access_sessions load error (run ASSESSMENT_ACCESS.sql):', error.message)
    setRows(data || []); setLoading(false)
  }
  if (isAuditor) return <div><div className="page-header"><h1>Assessment Access</h1></div><div className="notice notice-info" style={{ fontSize: 13 }}>Not available for the Auditor role.</div></div>
  if (openId) return <SessionDetail id={openId} onBack={() => { setOpenId(null); load() }} allRows={rows} />

  return (
    <div>
      <div className="page-header"><h1>Assessment Access</h1><CreateButton allRows={rows} onCreated={id => { load(); setOpenId(id) }} /></div>
      <div className="notice notice-info" style={{ fontSize: 13, marginBottom: 14 }}>
        Grant SAC/external assessors a time-limited, read-only link exposing only the jobs and controlled documents you select. Access is whitelist-based, logged, and revocable.
      </div>
      <div className="card" style={{ padding: 0 }}>
        <div className="table-wrap">
          <table>
            <thead><tr><th>Name</th><th>Assessor</th><th>Valid until</th><th>Status</th></tr></thead>
            <tbody>
              {loading && <tr><td colSpan={4} style={{ color: 'var(--text3)', textAlign: 'center', padding: 24 }}>Loading…</td></tr>}
              {!loading && rows.length === 0 && <tr><td colSpan={4} style={{ color: 'var(--text3)', textAlign: 'center', padding: 24 }}>No assessment sessions.</td></tr>}
              {rows.map(r => { const st = effStatus(r); return (
                <tr key={r.id} style={{ cursor: 'pointer' }} onClick={() => setOpenId(r.id)}>
                  <td style={{ fontWeight: 500 }}>{r.name || '—'}</td>
                  <td style={{ fontSize: 13 }}>{r.assessor_org || r.assessor_name || '—'}</td>
                  <td style={{ fontSize: 13, color: 'var(--text2)' }}>{fmtT(r.valid_until)}</td>
                  <td><span className={`badge ${st === 'Active' ? 'badge-ok' : st === 'Revoked' || st === 'Expired' ? 'badge-warn' : 'badge'}`}>{st}</span></td>
                </tr>
              ) })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

function CreateButton({ allRows, onCreated }) {
  const { profile, canApprove, logAction } = useAuth()
  const [busy, setBusy] = useState(false)
  if (!canApprove) return null
  async function create() {
    setBusy(true)
    const from = new Date(), until = new Date(Date.now() + 14 * 86400000)
    const { data, error } = await supabase.from('assessment_access_sessions').insert({
      name: `Assessment ${new Date().getFullYear()}`, valid_from: from.toISOString(), valid_until: until.toISOString(),
      status: 'Draft', created_by: profile?.name || 'Staff',
    }).select().single()
    setBusy(false)
    if (error) { failed("Couldn't create the assessor access", error.message); return }
    if (logAction) logAction('Assessment session created', 'assessment_access_sessions', data.id, data.name, {})
    onCreated(data.id)
  }
  return <button className="primary" disabled={busy} onClick={create}>+ New assessment session</button>
}

function SessionDetail({ id, onBack }) {
  const { profile, canApprove, logAction } = useAuth()
  const [s, setS] = useState(null)
  const [jobs, setJobs] = useState([])
  const [docs, setDocs] = useState([])
  const [log, setLog] = useState([])
  const [allDocs, setAllDocs] = useState([])
  const [picker, setPicker] = useState(false)
  const [link, setLink] = useState(null)
  const [saving, setSaving] = useState(false)
  const [iaRel, setIaRel] = useState([])   // released internal audits
  const [mrRel, setMrRel] = useState([])   // released management reviews
  const [allIAs, setAllIAs] = useState([])
  const [allMRs, setAllMRs] = useState([])
  const [samples, setSamples] = useState([])   // manual SHMS samples (+ their uploaded files)
  const [jobFiles, setJobFiles] = useState([]) // arbitrary docs attached to released jobs
  const [attachJob, setAttachJob] = useState(null)   // job row whose attachments modal is open
  const [showLog, setShowLog] = useState(false)      // access log folded by default (can be long)
  const ro = !canApprove

  useEffect(() => { load() }, [id])
  async function load() {
    const [{ data: ss }, { data: aj }, { data: ad }, { data: lg }, { data: cd }, { data: air }, { data: mrr }, { data: ia }, { data: mr }] = await Promise.all([
      supabase.from('assessment_access_sessions').select('*').eq('id', id).single(),
      supabase.from('assessment_access_jobs').select('*, jobs(scope, audit_type, audit_date, client_id, clients(name))').eq('session_id', id),
      supabase.from('assessment_access_documents').select('*, controlled_documents(document_code, title, document_type)').eq('session_id', id),
      supabase.from('assessment_access_log').select('*').eq('session_id', id).order('created_at', { ascending: false }).limit(200),
      supabase.from('controlled_documents').select('id, document_code, title, document_type').order('document_code'),
      supabase.from('assessment_access_internal_audits').select('*, internal_audits(ref, title, status)').eq('session_id', id),
      supabase.from('assessment_access_management_reviews').select('*, management_reviews(ref, review_date, status)').eq('session_id', id),
      supabase.from('internal_audits').select('id, ref, title, status').order('created_at', { ascending: false }),
      supabase.from('management_reviews').select('id, ref, review_date, status').order('created_at', { ascending: false }),
    ])
    setS(ss || null); setJobs(aj || []); setDocs(ad || []); setLog(lg || []); setAllDocs(cd || [])
    setIaRel(air || []); setMrRel(mrr || []); setAllIAs(ia || []); setAllMRs(mr || [])
    const { data: sm } = await supabase.from('assessment_manual_samples')
      .select('*, assessment_manual_files(*)').eq('session_id', id).order('sort_order').order('created_at')
    setSamples(sm || [])
    const { data: jf } = await supabase.from('assessment_job_files').select('*').eq('session_id', id).order('created_at')
    setJobFiles(jf || [])
  }
  const set = (k, v) => setS(p => ({ ...p, [k]: v }))

  async function saveBasics() {
    setSaving(true)
    const { error } = await supabase.from('assessment_access_sessions').update({
      name: s.name, valid_from: s.valid_from, valid_until: s.valid_until,
      assessor_org: s.assessor_org, assessor_name: s.assessor_name, assessor_contact: s.assessor_contact,
    }).eq('id', id)
    setSaving(false)
    if (error) { failed("Couldn't save the access settings", error.message); return }
    if (logAction) logAction('Assessment session updated', 'assessment_access_sessions', id, s.name, {})
    load()
  }
  async function generateLink() {
    const raw = randomToken()
    const hash = await sha256hex(raw)
    const { error } = await supabase.from('assessment_access_sessions').update({ token_hash: hash, token_prefix: raw.slice(0, 8), status: 'Active' }).eq('id', id)
    if (error) { failed("Couldn't create the access link", error.message); return }
    setLink(`${window.location.origin}/assessment/${raw}`)
    if (logAction) logAction('Assessment link generated', 'assessment_access_sessions', id, s.name, {})
    load()
  }
  async function revoke() {
    const reason = window.prompt('Revoke this assessment access? Reason (optional):', '')
    if (reason === null) return
    await supabase.from('assessment_access_sessions').update({ status: 'Revoked', revoked_by: profile?.name || 'Staff', revoked_at: new Date().toISOString(), revoke_reason: reason || null }).eq('id', id)
    if (logAction) logAction('Assessment session revoked', 'assessment_access_sessions', id, s.name, { reason })
    setLink(null); load()
  }
  async function addJobs(items) {
    const payload = items.map(j => ({ session_id: id, job_id: j.id, include_session_photo: true }))
    const { error } = await supabase.from('assessment_access_jobs').upsert(payload, { onConflict: 'session_id,job_id' })
    if (error) { failed("Couldn't add the jobs", error.message); return }
    if (logAction) logAction('Assessment jobs selected', 'assessment_access_sessions', id, s.name, { count: items.length })
    setPicker(false); load()
  }
  async function toggleJobPhoto(row) { await supabase.from('assessment_access_jobs').update({ include_session_photo: !row.include_session_photo }).eq('id', row.id); load() }
  async function removeJob(row) {
    // Also clear any assessment attachments for this job (storage + rows) so nothing is orphaned.
    const mine = jobFiles.filter(f => f.job_id === row.job_id)
    const paths = mine.map(f => f.storage_path).filter(Boolean)
    if (paths.length) await supabase.storage.from(CONSASS_BUCKET).remove(paths)
    if (mine.length) await supabase.from('assessment_job_files').delete().eq('session_id', id).eq('job_id', row.job_id)
    await supabase.from('assessment_access_jobs').delete().eq('id', row.id); load()
  }
  async function addJobFile(jobId, label, file) {
    if (!file || !label.trim()) return
    const safe = file.name.replace(/[^\w.\-]/g, '_')
    const path = `${id}/job/${jobId}/${Date.now()}_${safe}`
    const up = await supabase.storage.from(CONSASS_BUCKET).upload(path, file)
    if (up.error) { failed('Upload failed', up.error.message); return }
    const { error } = await supabase.from('assessment_job_files').insert({
      session_id: id, job_id: jobId, label: label.trim(), file_name: file.name, storage_path: path, sort_order: jobFiles.filter(f => f.job_id === jobId).length })
    if (error) { failed("Couldn't save", error.message); return }
    if (logAction) logAction('Assessment job document attached', 'assessment_access_sessions', id, s.name, { label })
    load()
  }
  async function removeJobFile(row) {
    if (!window.confirm('Remove this attached document?')) return
    if (row.storage_path) await supabase.storage.from(CONSASS_BUCKET).remove([row.storage_path])
    await supabase.from('assessment_job_files').delete().eq('id', row.id)
    load()
  }
  async function addDoc(document_id) {
    if (!document_id) return
    await supabase.from('assessment_access_documents').upsert({ session_id: id, document_id, revision_scope: 'current' }, { onConflict: 'session_id,document_id' })
    if (logAction) logAction('Assessment document selected', 'assessment_access_sessions', id, s.name, {})
    load()
  }
  async function addCurrentMP() {
    const mp = allDocs.find(d => d.document_type === 'M&P Manual')
    if (!mp) { toast('M&P Manual not found', 'warning', { detail: 'Upload it under Controlled Documents first' }); return }
    addDoc(mp.id)
  }
  async function toggleDocScope(row) { await supabase.from('assessment_access_documents').update({ revision_scope: row.revision_scope === 'all' ? 'current' : 'all' }).eq('id', row.id); load() }
  async function removeDoc(row) { await supabase.from('assessment_access_documents').delete().eq('id', row.id); load() }
  // Management-system releases
  async function toggleFlag(field) {
    const { error } = await supabase.from('assessment_access_sessions').update({ [field]: !s[field] }).eq('id', id)
    if (error) { failed("Couldn't change the document setting", error.message); return }
    if (logAction) logAction('Assessment register toggled', 'assessment_access_sessions', id, s.name, { [field]: !s[field] })
    load()
  }
  async function addIA(iaId) {
    if (!iaId) return
    await supabase.from('assessment_access_internal_audits').upsert({ session_id: id, internal_audit_id: iaId }, { onConflict: 'session_id,internal_audit_id' })
    if (logAction) logAction('Assessment internal audit released', 'assessment_access_sessions', id, s.name, {}); load()
  }
  async function removeIA(row) { await supabase.from('assessment_access_internal_audits').delete().eq('id', row.id); load() }
  async function addMR(mrId) {
    if (!mrId) return
    await supabase.from('assessment_access_management_reviews').upsert({ session_id: id, management_review_id: mrId }, { onConflict: 'session_id,management_review_id' })
    if (logAction) logAction('Assessment management review released', 'assessment_access_sessions', id, s.name, {}); load()
  }
  async function removeMR(row) { await supabase.from('assessment_access_management_reviews').delete().eq('id', row.id); load() }

  // ── ConSASS manual samples (company/project + 9 uploaded records) ─────────────────────────────────────
  async function addSample(form) {
    const { error } = await supabase.from('assessment_manual_samples').insert({
      session_id: id, company_name: form.company_name.trim(), project_name: form.project_name?.trim() || null,
      audit_start_date: form.audit_start_date || null, audit_end_date: form.audit_end_date || null, sort_order: samples.length,
    })
    if (error) { failed("Couldn't add the sample", error.message); return false }
    if (logAction) logAction('SHMS sample added', 'assessment_access_sessions', id, s.name, { company: form.company_name })
    load(); return true
  }
  async function removeSampleStorage(sample) {
    const paths = (sample.assessment_manual_files || []).map(f => f.storage_path).filter(Boolean)
    if (paths.length) await supabase.storage.from(CONSASS_BUCKET).remove(paths)
  }
  async function removeSample(sample) {
    if (!window.confirm(`Remove ${sample.company_name}${sample.project_name ? ' — ' + sample.project_name : ''} and its uploaded files? This cannot be undone.`)) return
    await removeSampleStorage(sample)
    await supabase.from('assessment_manual_samples').delete().eq('id', sample.id)   // cascades file rows
    if (logAction) logAction('SHMS sample removed', 'assessment_access_sessions', id, s.name, { company: sample.company_name })
    load()
  }
  async function uploadSlot(sample, docType, file) {
    if (!file) return
    const safe = file.name.replace(/[^\w.\-]/g, '_')
    const path = `${id}/${sample.id}/${docType}_${Date.now()}_${safe}`
    const up = await supabase.storage.from(CONSASS_BUCKET).upload(path, file)
    if (up.error) { failed('Upload failed', up.error.message); return }
    // One file per slot: drop any previous object for this slot, then replace the row. Update-if-exists /
    // insert-otherwise rather than upsert(onConflict) — the uniqueness rule on (sample_id, doc_type) is a
    // PARTIAL index (it excludes doc_type='other', so a sample can carry unlimited named "Other documents"),
    // and Postgres can't infer a partial index from a plain ON CONFLICT target, which is what was throwing
    // "no unique or exclusion constraint matching the ON CONFLICT specification" on every fixed-slot upload.
    const existing = (sample.assessment_manual_files || []).find(f => f.doc_type === docType)
    if (existing?.storage_path && existing.storage_path !== path) await supabase.storage.from(CONSASS_BUCKET).remove([existing.storage_path])
    const row = { sample_id: sample.id, doc_type: docType, file_name: file.name, storage_path: path, uploaded_at: new Date().toISOString() }
    const { error } = existing
      ? await supabase.from('assessment_manual_files').update(row).eq('id', existing.id)
      : await supabase.from('assessment_manual_files').insert(row)
    if (error) { failed("Couldn't save", error.message); return }
    if (logAction) logAction('SHMS file uploaded', 'assessment_access_sessions', id, s.name, { company: sample.company_name, doc: docType })
    load()
  }
  // Named "Other documents" — anything SAC asks for beyond the nine fixed records. Unlimited per sample:
  // these rows carry doc_type='other' and are exempt from the one-file-per-slot unique index (see
  // sql/ASSESSMENT_CONSASS_OTHER_DOCS.sql), so each is INSERTed rather than upserted onto a slot.
  async function uploadOther(sample, label, file) {
    if (!file || !label.trim()) return false
    const safe = file.name.replace(/[^\w.\-]/g, '_')
    const path = `${id}/${sample.id}/other_${Date.now()}_${safe}`
    const up = await supabase.storage.from(CONSASS_BUCKET).upload(path, file)
    if (up.error) { failed('Upload failed', up.error.message); return false }
    const others = (sample.assessment_manual_files || []).filter(f => f.doc_type === 'other')
    const { error } = await supabase.from('assessment_manual_files').insert({
      sample_id: sample.id, doc_type: 'other', label: label.trim(), file_name: file.name,
      storage_path: path, sort_order: others.length, uploaded_at: new Date().toISOString(),
    })
    if (error) {
      await supabase.storage.from(CONSASS_BUCKET).remove([path])   // don't strand the object
      failed("Couldn't save", error.message)
      return false
    }
    if (logAction) logAction('SHMS other document uploaded', 'assessment_access_sessions', id, s.name, { company: sample.company_name, doc: label.trim() })
    load(); return true
  }
  async function removeSlot(fileRow) {
    if (!window.confirm('Remove this uploaded file?')) return
    if (fileRow.storage_path) await supabase.storage.from(CONSASS_BUCKET).remove([fileRow.storage_path])
    await supabase.from('assessment_manual_files').delete().eq('id', fileRow.id)
    load()
  }
  async function purgeAllUploads() {
    if (!window.confirm('Delete ALL uploaded assessment files for this session — every SHMS company AND every document attached to released jobs?\nThis frees the storage space and cannot be undone.')) return
    for (const sm of samples) await removeSampleStorage(sm)
    await supabase.from('assessment_manual_samples').delete().eq('session_id', id)   // cascades file rows
    const jfPaths = jobFiles.map(f => f.storage_path).filter(Boolean)
    if (jfPaths.length) await supabase.storage.from(CONSASS_BUCKET).remove(jfPaths)
    await supabase.from('assessment_job_files').delete().eq('session_id', id)
    if (logAction) logAction('Assessment uploads purged', 'assessment_access_sessions', id, s.name, { samples: samples.length, jobFiles: jobFiles.length })
    load()
  }

  if (!s) return <div style={{ padding: 20, color: 'var(--text3)' }}>Loading…</div>
  const st = effStatus(s)
  const selectedDocIds = new Set(docs.map(d => d.document_id))

  return (
    <div>
      <div className="page-header"><h1>{s.name || 'Assessment session'} <span className={`badge ${st === 'Active' ? 'badge-ok' : 'badge-warn'}`} style={{ fontSize: 13 }}>{st}</span></h1><button onClick={onBack}>← Back</button></div>

      <div className="card" style={{ marginBottom: 12 }}>
        <h2 className="card-title">Session details</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
          <div className="field" style={{ gridColumn: '1 / -1' }}><label>Name</label><input value={s.name || ''} disabled={ro} onChange={e => set('name', e.target.value)} placeholder="e.g. SAC Assessment 2026 Desk Study" /></div>
          <div className="field"><label>Valid from</label><input type="datetime-local" value={(s.valid_from || '').slice(0, 16)} disabled={ro} onChange={e => set('valid_from', new Date(e.target.value).toISOString())} /></div>
          <div className="field"><label>Valid until</label><input type="datetime-local" value={(s.valid_until || '').slice(0, 16)} disabled={ro} onChange={e => set('valid_until', new Date(e.target.value).toISOString())} /></div>
          <div className="field"><label>Assessor organisation</label><input value={s.assessor_org || ''} disabled={ro} onChange={e => set('assessor_org', e.target.value)} /></div>
          <div className="field"><label>Assessor name</label><input value={s.assessor_name || ''} disabled={ro} onChange={e => set('assessor_name', e.target.value)} /></div>
          <div className="field"><label>Assessor contact</label><input value={s.assessor_contact || ''} disabled={ro} onChange={e => set('assessor_contact', e.target.value)} /></div>
        </div>
        {!ro && <div style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <button className="primary" onClick={saveBasics} disabled={saving}>{saving ? 'Saving…' : 'Save details'}</button>
          <button onClick={generateLink}>{s.token_hash ? 'Regenerate link' : 'Generate access link'}</button>
          {st === 'Active' && <button className="danger" onClick={revoke}>Revoke</button>}
        </div>}
        {link && <div style={{ marginTop: 10, padding: 10, background: 'var(--bg2)', borderRadius: 6 }}>
          <div style={{ fontSize: 13, color: 'var(--text3)', marginBottom: 4 }}>Copy this link now — it cannot be shown again (only its hash is stored). Regenerate to get a new one.</div>
          <input readOnly value={link} onClick={e => e.target.select()} style={{ width: '100%', fontSize: 13 }} />
        </div>}
        {s.status === 'Revoked' && <div style={{ marginTop: 8, fontSize: 13, color: 'var(--danger)' }}>Revoked by {s.revoked_by} on {fmtT(s.revoked_at)}{s.revoke_reason ? ` — ${s.revoke_reason}` : ''}</div>}
      </div>

      {/* Jobs */}
      <div className="card" style={{ marginBottom: 12, padding: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 14px' }}>
          <h2 className="card-title">WSH RM Audit sampled files ({jobs.length})</h2>{!ro && <button className="sm primary" onClick={() => setPicker(true)}>+ Select jobs</button>}
        </div>
        <div className="table-wrap"><table>
          <thead><tr><th>Job</th><th>Session photo</th><th>Attached documents</th>{!ro && <th></th>}</tr></thead>
          <tbody>
            {jobs.length === 0 && <tr><td colSpan={ro ? 3 : 4} style={{ textAlign: 'center', padding: 16, color: 'var(--text3)' }}>No jobs released.</td></tr>}
            {jobs.map(j => { const nDocs = jobFiles.filter(f => f.job_id === j.job_id).length; return (
              <tr key={j.id}>
                <td style={{ fontSize: 13 }}>{j.jobs?.clients?.name || 'Client'} — {j.jobs?.scope || j.jobs?.audit_type || 'audit'} {j.jobs?.audit_date ? `(${fmt(j.jobs.audit_date)})` : ''}</td>
                <td style={{ fontSize: 13 }}>{ro ? (j.include_session_photo ? 'Included' : 'Hidden') : <button className="sm" onClick={() => toggleJobPhoto(j)}>{j.include_session_photo ? 'Included ✓' : 'Hidden'}</button>}</td>
                <td style={{ fontSize: 13 }}><button className="sm" onClick={() => setAttachJob(j)}>{nDocs > 0 ? `Manage (${nDocs})` : 'Attach…'}</button></td>
                {!ro && <td><button className="sm danger" onClick={() => removeJob(j)}>Remove</button></td>}
              </tr>
            ) })}
          </tbody>
        </table></div>
      </div>

      {/* SHMS manual samples */}
      <ConsassCard ro={ro} samples={samples} hasUploads={samples.length > 0 || jobFiles.length > 0} onAdd={addSample} onRemoveSample={removeSample}
        onUpload={uploadSlot} onUploadOther={uploadOther} onRemoveFile={removeSlot} onPurge={purgeAllUploads} />

      {/* Documents */}
      <div className="card" style={{ marginBottom: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, flexWrap: 'wrap', gap: 8 }}>
          <h2 className="card-title">Released controlled documents ({docs.length})</h2>
          {!ro && <div style={{ display: 'flex', gap: 8 }}>
            <button className="sm" onClick={addCurrentMP}>+ Include current M&P</button>
            <select className="sm" defaultValue="" onChange={e => { addDoc(e.target.value); e.target.value = '' }}>
              <option value="">+ Add document…</option>
              {allDocs.filter(d => !selectedDocIds.has(d.id)).map(d => <option key={d.id} value={d.id}>{d.document_code} {d.title}</option>)}
            </select>
          </div>}
        </div>
        {docs.length === 0 && <div className="notice" style={{ fontSize: 13, background: '#FFF3E0', color: '#854F0B', border: '1px solid #E6C97A', borderRadius: 6, padding: '8px 12px', marginBottom: 10 }}>
          Warning: No controlled documents selected. SAC normally requests the current M&P and related controlled documents for desk study. Use “+ Include current M&P” or “+ Add document…”.
        </div>}
        <div className="table-wrap"><table>
          <thead><tr><th>Document</th><th>Revisions</th>{!ro && <th></th>}</tr></thead>
          <tbody>
            {docs.length === 0 && <tr><td colSpan={ro ? 2 : 3} style={{ textAlign: 'center', padding: 16, color: 'var(--text3)' }}>No documents released.</td></tr>}
            {docs.map(d => (
              <tr key={d.id}>
                <td style={{ fontSize: 13 }}>{d.controlled_documents?.document_code} {d.controlled_documents?.title} <span style={{ color: 'var(--text3)', fontSize: 13 }}>({d.controlled_documents?.document_type})</span></td>
                <td style={{ fontSize: 13 }}>{ro ? (d.revision_scope === 'all' ? 'All revisions' : 'Current only') : <button className="sm" onClick={() => toggleDocScope(d)}>{d.revision_scope === 'all' ? 'All revisions' : 'Current only'}</button>}</td>
                {!ro && <td><button className="sm danger" onClick={() => removeDoc(d)}>Remove</button></td>}
              </tr>
            ))}
          </tbody>
        </table></div>
      </div>

      {/* Management system (registers & records) */}
      <div className="card" style={{ marginBottom: 12 }}>
        <h2 className="card-title">Management system</h2>
        <div style={{ fontSize: 13, color: 'var(--text3)', marginBottom: 12 }}>Release the organisation-level records for the assessor. Registers are whole-or-nothing (an assessor expects a complete register); internal audits and management reviews are chosen individually.</div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 14 }}>
          {[['include_impartiality', 'Impartiality Register'], ['include_complaints', 'Complaints'], ['include_personnel', 'Personnel & Competence']].map(([field, label]) => (
            <label key={field} style={{ display: 'flex', gap: 10, alignItems: 'center', fontSize: 13, cursor: ro ? 'default' : 'pointer' }}>
              <input type="checkbox" checked={!!s[field]} disabled={ro} onChange={() => toggleFlag(field)} />
              <span>{label}</span>
              <span className={`badge ${s[field] ? 'badge-ok' : 'badge-gray'}`} style={{ marginLeft: 'auto' }}>{s[field] ? 'Released' : 'Not released'}</span>
            </label>
          ))}
        </div>

        {/* Internal audits */}
        <div style={{ borderTop: '1px solid var(--border)', paddingTop: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, flexWrap: 'wrap', gap: 8 }}>
            <h3 style={{ margin: 0 }}>Internal audits ({iaRel.length})</h3>
            {!ro && <select className="sm" defaultValue="" onChange={e => { addIA(e.target.value); e.target.value = '' }} style={{ maxWidth: 320 }}>
              <option value="">+ Release an internal audit…</option>
              {allIAs.filter(x => !iaRel.some(r => r.internal_audit_id === x.id)).map(x => <option key={x.id} value={x.id}>{x.ref}{x.title ? ' — ' + x.title : ''} ({x.status})</option>)}
            </select>}
          </div>
          {iaRel.length === 0 ? <div style={{ fontSize: 13, color: 'var(--text3)' }}>None released.</div> : (
            <div className="table-wrap"><table>
              <thead><tr><th>Ref</th><th>Title</th><th>Status</th>{!ro && <th></th>}</tr></thead>
              <tbody>{iaRel.map(r => (
                <tr key={r.id}><td style={{ fontSize: 13, fontWeight: 500 }}>{r.internal_audits?.ref || '—'}</td>
                  <td style={{ fontSize: 13 }}>{r.internal_audits?.title || '—'}</td><td style={{ fontSize: 13 }}>{r.internal_audits?.status || '—'}</td>
                  {!ro && <td><button className="sm danger" onClick={() => removeIA(r)}>Remove</button></td>}</tr>
              ))}</tbody>
            </table></div>
          )}
        </div>

        {/* Management reviews */}
        <div style={{ borderTop: '1px solid var(--border)', paddingTop: 12, marginTop: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, flexWrap: 'wrap', gap: 8 }}>
            <h3 style={{ margin: 0 }}>Management reviews ({mrRel.length})</h3>
            {!ro && <select className="sm" defaultValue="" onChange={e => { addMR(e.target.value); e.target.value = '' }} style={{ maxWidth: 320 }}>
              <option value="">+ Release a management review…</option>
              {allMRs.filter(x => !mrRel.some(r => r.management_review_id === x.id)).map(x => <option key={x.id} value={x.id}>{x.ref} ({x.status})</option>)}
            </select>}
          </div>
          {mrRel.length === 0 ? <div style={{ fontSize: 13, color: 'var(--text3)' }}>None released.</div> : (
            <div className="table-wrap"><table>
              <thead><tr><th>Ref</th><th>Review date</th><th>Status</th>{!ro && <th></th>}</tr></thead>
              <tbody>{mrRel.map(r => (
                <tr key={r.id}><td style={{ fontSize: 13, fontWeight: 500 }}>{r.management_reviews?.ref || '—'}</td>
                  <td style={{ fontSize: 13 }}>{fmt(r.management_reviews?.review_date)}</td><td style={{ fontSize: 13 }}>{r.management_reviews?.status || '—'}</td>
                  {!ro && <td><button className="sm danger" onClick={() => removeMR(r)}>Remove</button></td>}</tr>
              ))}</tbody>
            </table></div>
          )}
        </div>
      </div>

      {/* Access log (folded by default — can be hundreds of rows) */}
      <div className="card" style={{ padding: 0 }}>
        <button onClick={() => setShowLog(v => !v)} style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', border: 'none', background: 'none', cursor: 'pointer', padding: '12px 14px', textAlign: 'left' }}>
          <span style={{ width: 12, color: 'var(--text3)', fontSize: 13 }}>{showLog ? '▾' : '▸'}</span>
          <h2 className="card-title" style={{ flex: 1 }}>Access log ({log.length})</h2>
          <span style={{ color: 'var(--brand)', fontSize: 13, fontWeight: 500 }}>{showLog ? 'Hide' : 'Show'}</span>
        </button>
        {showLog && <div className="table-wrap"><table>
          <thead><tr><th>When</th><th>Action</th><th>Who (self-declared)</th><th>Entity</th><th>IP</th></tr></thead>
          <tbody>
            {log.length === 0 && <tr><td colSpan={5} style={{ textAlign: 'center', padding: 16, color: 'var(--text3)' }}>No access yet.</td></tr>}
            {log.map(l => (
              <tr key={l.id}>
                <td style={{ fontSize: 13, color: 'var(--text2)' }}>{fmtT(l.created_at)}</td>
                <td style={{ fontSize: 13 }}>{l.action}</td>
                <td style={{ fontSize: 13 }}>{l.assessor_name || '—'}{l.assessor_email ? ` (${l.assessor_email})` : ''}</td>
                <td style={{ fontSize: 13, color: 'var(--text2)', maxWidth: 220 }}>{l.entity_label || l.entity_type || '—'}</td>
                <td style={{ fontSize: 13, color: 'var(--text3)' }}>{l.ip || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table></div>}
      </div>

      {picker && <JobPicker existing={jobs.map(j => j.job_id)} onClose={() => setPicker(false)} onAdd={addJobs} />}
      {attachJob && <JobAttachModal job={attachJob} ro={ro} files={jobFiles.filter(f => f.job_id === attachJob.job_id)}
        onClose={() => setAttachJob(null)} onAdd={addJobFile} onRemove={removeJobFile} />}
    </div>
  )
}

function JobPicker({ existing, onClose, onAdd }) {
  const [cands, setCands] = useState([])
  const [loading, setLoading] = useState(true)
  const [q, setQ] = useState('')
  const [sel, setSel] = useState({})
  const existingSet = new Set(existing || [])
  useEffect(() => { (async () => {
    const { data } = await supabase.from('jobs').select('id, scope, audit_type, audit_date, status, client_id, clients(name)').order('audit_date', { ascending: false }).limit(1000)
    // Client Internal Audit jobs are outside the accredited scope — never offered for release to an assessor; nor is
    // a cancelled audit (it never took place).
    setCands((data || []).filter(j => !isOtherService(j.audit_type) && j.status !== 'Cancelled').map(j => ({ id: j.id, label: `${j.clients?.name || 'Client'} — ${j.scope || j.audit_type || 'audit'}${j.audit_date ? ` (${fmt(j.audit_date)})` : ''}`, search: `${j.clients?.name || ''} ${j.scope || ''} ${j.audit_type || ''}`.toLowerCase() })))
    setLoading(false)
  })() }, [])
  const filtered = cands.filter(c => !q || c.search.includes(q.toLowerCase()))
  const chosen = filtered.filter(c => sel[c.id] && !existingSet.has(c.id))
  return (
    <div className="modal-overlay" onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '40px 16px', overflowY: 'auto', zIndex: 60 }}>
      <div className="card" onClick={e => e.stopPropagation()} style={{ maxWidth: 700, width: '100%' }}>
        <h2 style={{ marginBottom: 8 }}>Select jobs to release</h2>
        <input placeholder="Filter by client / scope…" value={q} onChange={e => setQ(e.target.value)} style={{ width: '100%', marginBottom: 10 }} />
        <div className="table-wrap" style={{ maxHeight: 360, overflowY: 'auto' }}><table><tbody>
          {loading && <tr><td style={{ padding: 14, color: 'var(--text3)' }}>Loading…</td></tr>}
          {!loading && filtered.map(c => { const already = existingSet.has(c.id); return (
            <tr key={c.id} style={{ opacity: already ? 0.5 : 1 }}>
              <td style={{ width: 28 }}><input type="checkbox" disabled={already} checked={!!sel[c.id]} onChange={() => setSel(s => ({ ...s, [c.id]: !s[c.id] }))} /></td>
              <td style={{ fontSize: 13 }}>{c.label}{already ? ' (added)' : ''}</td>
            </tr>
          ) })}
        </tbody></table></div>
        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <button className="primary" onClick={() => chosen.length ? onAdd(chosen) : toast('Tick at least one job', 'warning')}>Add {chosen.length || ''} selected</button>
          <button onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  )
}

// ── ConSASS manual samples: key company/project/dates, then upload the 9 records into fixed slots ─────────
function ConsassCard({ ro, samples, hasUploads, onAdd, onRemoveSample, onUpload, onUploadOther, onRemoveFile, onPurge }) {
  const [adding, setAdding] = useState(false)
  const [form, setForm] = useState({ company_name: '', project_name: '', audit_start_date: '', audit_end_date: '' })
  const setF = (k, v) => setForm(f => ({ ...f, [k]: v }))
  async function save() {
    if (!form.company_name.trim()) { toast('Enter the company name', 'warning'); return }
    const ok = await onAdd(form)
    if (ok) { setForm({ company_name: '', project_name: '', audit_start_date: '', audit_end_date: '' }); setAdding(false) }
  }
  async function viewFile(path) {
    const { data, error } = await supabase.storage.from(CONSASS_BUCKET).createSignedUrl(path, 300)
    if (error || !data?.signedUrl) { failed("Couldn't open the file", error?.message); return }
    window.open(data.signedUrl, '_blank', 'noopener')
  }

  return (
    <div className="card" style={{ marginBottom: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4, flexWrap: 'wrap', gap: 8 }}>
        <h2 className="card-title">SHMS sampled files ({samples.length})</h2>
        {!ro && <button className="sm primary" onClick={() => setAdding(a => !a)}>{adding ? 'Close' : '+ Add company / project'}</button>}
      </div>
      <div style={{ fontSize: 13, color: 'var(--text3)', marginBottom: 12 }}>
        SHMS audits aren’t run in AuditPM, so enter each sampled company here and upload its records. The assessor sees one simple block of nine documents per company. Files stay private and can be cleared after the assessment.
      </div>

      {adding && !ro && (
        <div style={{ background: 'var(--bg2)', borderRadius: 8, padding: 12, marginBottom: 12 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 10 }}>
            <div className="field" style={{ gridColumn: '1 / -1' }}><label>Company name *</label><input value={form.company_name} onChange={e => setF('company_name', e.target.value)} placeholder="Audited company" /></div>
            <div className="field" style={{ gridColumn: '1 / -1' }}><label>Project / worksite name</label><input value={form.project_name} onChange={e => setF('project_name', e.target.value)} placeholder="e.g. project or site name" /></div>
            <div className="field"><label>Audit start date</label><input type="date" value={form.audit_start_date} onChange={e => setF('audit_start_date', e.target.value)} /></div>
            <div className="field"><label>Audit end date</label><input type="date" value={form.audit_end_date} onChange={e => setF('audit_end_date', e.target.value)} /></div>
          </div>
          <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
            <button className="sm primary" onClick={save}>Add company</button>
            <button className="sm" onClick={() => setAdding(false)}>Cancel</button>
          </div>
        </div>
      )}

      {samples.length === 0 ? (
        <div style={{ fontSize: 13, color: 'var(--text3)' }}>No companies added yet.</div>
      ) : samples.map(sm => {
        const byType = {}; (sm.assessment_manual_files || []).forEach(f => { byType[f.doc_type] = f })
        const done = CONSASS_DOC_TYPES.filter(([k]) => byType[k]).length
        const dates = [sm.audit_start_date, sm.audit_end_date].filter(Boolean).map(fmt)
        const dateStr = dates.length === 2 ? (dates[0] === dates[1] ? dates[0] : `${dates[0]} – ${dates[1]}`) : (dates[0] || '')
        return (
          <div key={sm.id} style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 12, marginBottom: 10 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
              <div>
                <div style={{ fontWeight: 600 }}>{sm.company_name}</div>
                <div style={{ fontSize: 13, color: 'var(--text2)' }}>{sm.project_name || 'No project name'}{dateStr ? ` · ${dateStr}` : ''}</div>
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <span className={`badge ${done === CONSASS_DOC_TYPES.length ? 'badge-ok' : 'badge-gray'}`}>{done}/{CONSASS_DOC_TYPES.length} uploaded</span>
                {!ro && <button className="sm danger" onClick={() => onRemoveSample(sm)}>Remove</button>}
              </div>
            </div>
            <div className="table-wrap"><table>
              <tbody>
                {CONSASS_DOC_TYPES.map(([key, label], i) => {
                  const f = byType[key]
                  return (
                    <tr key={key}>
                      <td style={{ fontSize: 13, width: 24, color: 'var(--text3)' }}>{i + 1}</td>
                      <td style={{ fontSize: 13, fontWeight: 500 }}>{label}</td>
                      <td style={{ fontSize: 13, color: f ? 'var(--text2)' : 'var(--text3)' }}>
                        {f ? <button className="sm" onClick={() => viewFile(f.storage_path)} title={f.file_name}>View {f.file_name ? '· ' + (f.file_name.length > 28 ? f.file_name.slice(0, 27) + '…' : f.file_name) : ''}</button> : 'Not uploaded'}
                      </td>
                      {!ro && <td style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
                        <label className="sm" style={{ cursor: 'pointer', display: 'inline-block', padding: '2px 8px', border: '1px solid var(--border)', borderRadius: 6 }}>
                          {f ? 'Replace' : 'Upload'}
                          <input type="file" style={{ display: 'none' }} onChange={e => { const file = e.target.files?.[0]; e.target.value = ''; if (file) onUpload(sm, key, file) }} />
                        </label>
                        {f && <button className="sm danger" style={{ marginLeft: 6 }} onClick={() => onRemoveFile(f)}>Remove</button>}
                      </td>}
                    </tr>
                  )
                })}
              </tbody>
            </table></div>
            <OtherDocs sample={sm} ro={ro} viewFile={viewFile} onUploadOther={onUploadOther} onRemoveFile={onRemoveFile} />
          </div>
        )
      })}

      {!ro && hasUploads && (
        <div style={{ marginTop: 6, borderTop: '1px solid var(--border)', paddingTop: 10 }}>
          <button className="sm danger" onClick={onPurge}>Delete all assessment uploads (free storage)</button>
          <span style={{ fontSize: 13, color: 'var(--text3)', marginLeft: 8 }}>After the assessment, removes every SHMS company file AND every document attached to released jobs.</span>
        </div>
      )}
    </div>
  )
}

// Attach arbitrary NAMED documents to a released WSH RM Audit job — for anything SAC wants to see that
// isn't captured in AuditPM (e.g. the signed quotation). Shown in that job's pack on the assessor portal.
function JobAttachModal({ job, ro, files, onClose, onAdd, onRemove }) {
  const [label, setLabel] = useState('')
  const [file, setFile] = useState(null)
  const [busy, setBusy] = useState(false)
  const jobLabel = `${job.jobs?.clients?.name || 'Client'} — ${job.jobs?.scope || job.jobs?.audit_type || 'audit'}`
  async function add() {
    if (!label.trim()) { toast('Name the document', 'warning', { detail: 'For example "Signed quotation"' }); return }
    if (!file) { toast('Choose a file', 'warning'); return }
    setBusy(true); await onAdd(job.job_id, label, file); setBusy(false); setLabel(''); setFile(null)
  }
  return (
    <div className="modal-overlay" onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '40px 16px', overflowY: 'auto', zIndex: 60 }}>
      <div className="card" onClick={e => e.stopPropagation()} style={{ maxWidth: 620, width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
          <div>
            <h2 style={{ margin: 0 }}>Attached documents</h2>
            <div style={{ fontSize: 13, color: 'var(--text2)', marginTop: 2 }}>{jobLabel}</div>
          </div>
          <button className="sm" onClick={onClose}>Close</button>
        </div>
        <div style={{ fontSize: 13, color: 'var(--text3)', margin: '8px 0 12px' }}>
          Attach anything the assessor may ask to see that isn’t already in AuditPM — the signed quotation, extra evidence, correspondence. These appear in this job’s pack on the assessor portal.
        </div>

        {files.length === 0 ? <div style={{ fontSize: 13, color: 'var(--text3)', marginBottom: 12 }}>No documents attached yet.</div> : (
          <div className="table-wrap" style={{ marginBottom: 12 }}><table>
            <tbody>{files.map(f => (
              <tr key={f.id}>
                <td style={{ fontSize: 13, fontWeight: 500 }}>{f.label}</td>
                <td style={{ fontSize: 13, color: 'var(--text3)' }}>{f.file_name || '—'}</td>
                {!ro && <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}><button className="sm danger" onClick={() => onRemove(f)}>Remove</button></td>}
              </tr>
            ))}</tbody>
          </table></div>
        )}

        {!ro && (
          <div style={{ background: 'var(--bg2)', borderRadius: 8, padding: 12 }}>
            <div className="field" style={{ marginBottom: 8 }}><label>Document name</label>
              <input value={label} onChange={e => setLabel(e.target.value)} placeholder='e.g. Signed quotation' /></div>
            <div className="field" style={{ marginBottom: 10 }}><label>File</label>
              <input type="file" onChange={e => setFile(e.target.files?.[0] || null)} /></div>
            <button className="sm primary" onClick={add} disabled={busy}>{busy ? 'Uploading…' : '+ Attach document'}</button>
          </div>
        )}
      </div>
    </div>
  )
}

// ── "Other documents" for one SHMS sample: name it, upload it, as many as SAC asks for ────────────
// Each row is a doc_type='other' file with its own label. Naming first is deliberate: the label is what the
// assessor sees in the portal, so an unnamed upload would arrive as an anonymous file.
function OtherDocs({ sample, ro, viewFile, onUploadOther, onRemoveFile }) {
  const [label, setLabel] = useState('')
  const [busy, setBusy] = useState(false)
  const others = (sample.assessment_manual_files || [])
    .filter(f => f.doc_type === 'other')
    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
  async function pick(file) {
    if (!label.trim()) { toast('Name the document first', 'warning', { detail: 'For example "Training records requested by SAC"' }); return }
    setBusy(true)
    const ok = await onUploadOther(sample, label, file)
    setBusy(false)
    if (ok) setLabel('')
  }
  return (
    <div style={{ marginTop: 10, borderTop: '1px solid var(--border)', paddingTop: 10 }}>
      <div style={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--text3)', marginBottom: 6 }}>
        Other documents ({others.length})
      </div>
      {others.length > 0 && (
        <div className="table-wrap"><table>
          <tbody>
            {others.map((f, i) => (
              <tr key={f.id}>
                <td style={{ fontSize: 13, width: 24, color: 'var(--text3)' }}>{i + 1}</td>
                <td style={{ fontSize: 13, fontWeight: 500 }}>{f.label || f.file_name || 'Untitled document'}</td>
                <td style={{ fontSize: 13, color: 'var(--text2)' }}>
                  <button className="sm" onClick={() => viewFile(f.storage_path)} title={f.file_name}>View {f.file_name ? '· ' + (f.file_name.length > 28 ? f.file_name.slice(0, 27) + '…' : f.file_name) : ''}</button>
                </td>
                {!ro && <td style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
                  <button className="sm danger" onClick={() => onRemoveFile(f)}>Remove</button>
                </td>}
              </tr>
            ))}
          </tbody>
        </table></div>
      )}
      {!ro && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: others.length ? 8 : 0, flexWrap: 'wrap' }}>
          <input value={label} onChange={e => setLabel(e.target.value)} placeholder="Name this document (e.g. SAC request — training records)"
            style={{ flex: 1, minWidth: 220, fontSize: 13 }} />
          <label className="sm" style={{ cursor: busy ? 'wait' : 'pointer', display: 'inline-block', padding: '4px 10px', border: '1px solid var(--border)', borderRadius: 6, opacity: busy ? 0.6 : 1, whiteSpace: 'nowrap' }}>
            {busy ? 'Uploading…' : '+ Upload document'}
            <input type="file" style={{ display: 'none' }} disabled={busy}
              onChange={e => { const file = e.target.files?.[0]; e.target.value = ''; if (file) pick(file) }} />
          </label>
        </div>
      )}
    </div>
  )
}
